import { useState, type ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import type {
  TableQueryParams,
  TableSortUrlProps,
} from "@/v2-backoffice-client/components/table"
import UserLoginTable from "@/v2-backoffice-client/userLogin/list/UserLoginTable"
import { useUserLoginListQuery } from "@/v2-backoffice-client/userLogin/list/userLogin.list.query"
import {
  deriveSessionFilters,
  type SessionFilters,
} from "@/v2-backoffice-client/userLogin/userLogin.filters"

const PAGE_SIZE = 100

/** Default table id of the fleet-wide (unscoped) instance on /activity. */
export const USER_LOGIN_TABLE_ID = "user-login-table"

export type UserLoginPanelProps = ComponentProps<"div"> & {
  /** Scope to a single user (per-user panel; hides the User column/search). */
  userId?: number
  /** Scope to a team's (non-deleted) members. */
  tenantId?: number
  /** Scope to the members of a customer's (non-deleted) teams. */
  customerId?: number
  tableId?: string
  /**
   * Controlled-sorting wiring forwarded to the table, e.g. from
   * `useTableSortUrl`. Passed per placement so only the call sites that want
   * URL-bookmarkable sorting (the Activity page) opt in — scoped sheet
   * instances stay URL-free.
   */
  sortProps?: TableSortUrlProps
}

// One acc.UserLogins list, optionally scoped to a user / team / customer:
// server-filtered + paged + infinite-loaded, with multi-select revoke. This is
// the single "activity" surface — the Activity page renders it fleet-wide, the
// user / team / customer panels render it scoped — so every placement gets the
// same columns (incl. Version) and behavior. Owns only its filter state; the
// table owns selection + revoke.
function UserLoginPanel(props: UserLoginPanelProps) {
  const {
    userId,
    tenantId,
    customerId,
    tableId = USER_LOGIN_TABLE_ID,
    sortProps,
    className,
    ...divProps
  } = props

  const [filters, setFilters] = useState<SessionFilters>({})
  const input = {
    userId,
    tenantId,
    customerId,
    limit: PAGE_SIZE,
    offset: 0,
    ...filters,
  }

  const { data, loading, error, refetch, fetchMore } = useUserLoginListQuery({
    variables: { input },
    notifyOnNetworkStatusChange: true,
  })

  const items = data?.getUserLogins.items ?? []
  const pageInfo = data?.getUserLogins.pageInfo ?? null

  function handleQueryChange(params: TableQueryParams) {
    const next = deriveSessionFilters(params)
    setFilters((prev) =>
      JSON.stringify(prev) === JSON.stringify(next) ? prev : next,
    )
  }

  return (
    <div className={cn("user-login-panel", className)} {...divProps}>
      <UserLoginTable
        hideUser={userId != null}
        tableId={tableId}
        logins={items}
        isLoading={loading}
        error={error}
        pageInfo={pageInfo}
        onFetchMore={() =>
          fetchMore({
            variables: { input: { ...input, offset: items.length } },
          })
        }
        onRefresh={refetch}
        manualFiltering
        searchColumn={userId == null ? "Search" : undefined}
        onQueryChange={handleQueryChange}
        {...sortProps}
      />
    </div>
  )
}

export default UserLoginPanel
